/* eslint-disable arrow-body-style */
/* global G */
import { curry } from 'lib/util'
import infinite from 'app/_shared/events/search/infinite'

type SearchOptions = {
  type?: string,
  limit?: number
  page?: string | number,
}

/**
 *
 * @param a
 * @param b
 */
const bySubmitDate = (a, b) => (b?.value?.submitDate && a?.value?.submitDate
  ? new Date(b.value.submitDate).valueOf() - new Date(a.value.submitDate).valueOf()
  : 0)

/**
 * Attempts to obtain a local list of domain objects according to the configuration of the current
 * module's model.
 *
 * @param module          current module
 * @param component       event source
 * @param eventOrOptions  triggered event
 */
const getLocalList = async (
  module: Gaia.AppModule.Spec,
  component: Gaia.Component.Spec,
  eventOrOptions: Gaia.PlatformEvent | SearchOptions,
) => {
  const model = module[G.MODEL]
  const options = eventOrOptions?.detail || eventOrOptions || {}
  const { type: eventType, limit: eventLimit, page = 0 } = options
  const { api: modelType } = model[G.PROPS]
  const { api: { type: componentType }, limit: componentLimit } = component[G.PROPS]
  const { drafts } = module[G.ADAPTER][G.PERSISTENCE]

  const type = componentType || eventType || modelType
  const limit = componentLimit ?? eventLimit
  const localList = await drafts.getAll({ type })

  const data = localList.slice(page * limit, limit).map((item: any) => {
    const { data, action } = item
    data.value.delete = action === 'delete' // makes `mergeLists` hide them after being considered
    data.value.draft = true
    return data
  })

  if (localList.length > data.length) {
    return { data, page: page + 1}
  }

  return { data, page }
}

/**
 * Attempts to obtain a remote list of domain objects, according to the `component` configuration
 * and the current module's model.
 *
 * @param key             listing type (e.g. short, verbose, etc.)
 * @param module          current module
 * @param component       component that triggered the event
 * @param eventOrOptions  the event object
 */
const getRemoteList = async (
  key: string | null,
  module: Gaia.AppModule.Spec,
  component: Gaia.Component.Spec,
  eventOrOptions: Gaia.PlatformEvent | SearchOptions,
) => {
  // const model = module[G.MODEL]
  // const httpAdapter = model[G.ADAPTER][G.HTTP]
  try {
    // const options = { middleware: ({ error, ...middleware }) => Object.values(middleware) }
    // const { data = [] } = await httpAdapter.withContext(options, async () => {
    //   return infinite(key, module, component, eventOrOptions)
    // })
    const { data: rawData = [], page } = await infinite(key, module, component, eventOrOptions) as any
    const data = rawData.filter(item => item.key && item.key !== -1 && !item?.value?.delete)
    return { data, page }
  } catch (e) {
    console.log('Unable to get remote list:', e)
    return { data: [], page: null }
  }
}

/**
 * Merges `localList` and `remoteList` by filtering out all items of the latter found in the former
 * and, afterward, also those (local ones) marked to be deleted.
 *
 * @param localList  list of domain objects obtained locally
 * @param remoteList list of domain objects obtained remotely
 */
const mergeLists = (
  localList: { data: [], page: string | number },
  remoteList: { data: [], page: string | number },
) => {
  const ids: string[] = []
  const page = localList.page ?? remoteList.page

  return localList.data.concat(remoteList.data).sort(bySubmitDate).reduce(({ data, page }, item) => {
    const { key } = item
    if (!ids.includes(key)) {
      ids.push(key)
      data.push(item)
    }
    return { data, page }
  }, { data: [], page })
}

/**
 * Returns a paginated list of remote and local documents.
 *
 * @param key             listing type, ie short, verbose, etc
 * @param module          current module
 * @param component       component that triggered the event
 * @param eventOrOptions  event information
 */
const infiniteWithDrafts = async (
  key: string | null,
  module: Gaia.AppModule.Spec,
  component: Gaia.Component.Spec,
  eventOrOptions: Gaia.PlatformEvent | object,
) => {
  const eventOptions = eventOrOptions?.detail || eventOrOptions || {}
  const { limit: eventLimit, type: eventType, page } = eventOptions
  const { api: { type: componentType }, limit: componentLimit } = component[G.PROPS]
  const limit = eventLimit ?? componentLimit ?? 10
  const type = eventType || componentType

  const options: SearchOptions = { type, limit, page }

  const localList = await getLocalList(module, component, options)

  if (localList.data.length < limit) {
    options.limit = limit - localList.data.length
    const remoteList = await getRemoteList(key, module, component, options)
    return mergeLists(localList, remoteList)
  }

  return localList
}

export default curry(infiniteWithDrafts)
